/**
 * Offline Cache Service - Cachea conversaciones en localStorage
 * para acceso offline. No modifica el comportamiento online existente.
 */

const CACHE_KEY = 'damyxia_offline_conversations';
const CACHE_LIST_KEY = 'damyxia_offline_conv_list';
const CACHE_VERSION_KEY = 'damyxia_offline_version';
const MAX_CACHED_CONVERSATIONS = 20;
const CACHE_VERSION = 1;

/**
 * Guarda la lista de conversaciones (sin mensajes) para el sidebar
 */
export function cacheConversationList(conversations) {
  try {
    const slim = conversations.map(c => ({
      id: c.id,
      title: c.title,
      updated_at: c.updated_at,
      message_count: c.message_count || 0
    }));
    localStorage.setItem(CACHE_LIST_KEY, JSON.stringify(slim));
    localStorage.setItem(CACHE_VERSION_KEY, String(CACHE_VERSION));
  } catch (e) {
    console.warn('[OfflineCache] Error caching conversation list:', e);
  }
}

/**
 * Recupera la lista de conversaciones cacheadas
 */
export function getCachedConversationList() {
  try {
    const data = localStorage.getItem(CACHE_LIST_KEY);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    return [];
  }
}

/**
 * Guarda una conversación completa (con mensajes) en el cache
 */
export function cacheConversation(conversation) {
  try {
    const cache = getAllCachedConversations();
    cache[conversation.id] = {
      ...conversation,
      _cachedAt: Date.now()
    };

    // Limitar el tamaño del cache
    const entries = Object.entries(cache);
    if (entries.length > MAX_CACHED_CONVERSATIONS) {
      // Eliminar las más antiguas
      entries.sort((a, b) => (a[1]._cachedAt || 0) - (b[1]._cachedAt || 0));
      const toRemove = entries.slice(0, entries.length - MAX_CACHED_CONVERSATIONS);
      toRemove.forEach(([key]) => delete cache[key]);
    }

    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.warn('[OfflineCache] Error caching conversation:', e);
    // Si localStorage está lleno, limpiar el cache
    if (e.name === 'QuotaExceededError') {
      clearConversationCache();
    }
  }
}

/**
 * Recupera una conversación cacheada por ID
 */
export function getCachedConversation(conversationId) {
  try {
    const cache = getAllCachedConversations();
    return cache[conversationId] || null;
  } catch (e) {
    return null;
  }
}

/**
 * Obtiene todas las conversaciones cacheadas
 */
function getAllCachedConversations() {
  try {
    const data = localStorage.getItem(CACHE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (e) {
    return {};
  }
}

/**
 * Limpia todo el cache de conversaciones
 */
export function clearConversationCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_LIST_KEY);
  } catch (e) {
    // Silently fail
  }
}

/**
 * Detecta si el navegador está offline
 */
export function isOffline() {
  return !navigator.onLine;
}

/**
 * Registra listeners para cambios de conectividad
 * Retorna una función de cleanup
 */
export function onConnectivityChange(callback) {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}
